import { MAX_NAME_PART_BYTES } from "../protocol/names.ts";

/**
 * The name lookup's shared rules (ADR-068's amendment "chat commands, Discord style", item 3): a full name is
 * looked up by its two parts in lower case joined by one space, typed text becomes a prefix of that key, and the
 * page and the gateway hold a lookup to the same numbers. The page matches the names in its own view with the
 * same key the gateway's index sorts by, so the two never disagree about what a prefix names.
 */

/**
 * The fewest characters a name option holds before the page asks the gateway.
 */
export const MIN_LOOKUP_CHARS = 2;

/**
 * The longest prefix a lookup carries: two name parts at the wire's bound and the space between them.
 */
export const MAX_LOOKUP_PREFIX_CHARS = 2 * MAX_NAME_PART_BYTES + 1;

/**
 * The most names a lookup answers, and the most a name option shows.
 */
export const MAX_NAME_MATCHES = 8;

/**
 * How many lookups one account may make in a window of `NAME_LOOKUP_WINDOW_MS`, whatever its connections.
 */
export const NAME_LOOKUPS_PER_WINDOW = 10;

/**
 * The account's lookup window, in milliseconds.
 */
export const NAME_LOOKUP_WINDOW_MS = 10_000;

/**
 * White space at the start of typed text.
 */
const LEADING_SPACE = /^\s+/;

/**
 * A run of white space inside typed text.
 */
const SPACE_RUN = /\s+/g;

/**
 * The key a full name is looked up by.
 *
 * @param first The first name.
 * @param family The family name.
 * @returns Both parts in lower case, joined by one space.
 * @example
 * fullNameKey("Kaelen", "Ashgrove");
 * // "kaelen ashgrove"
 */
export function fullNameKey(first: string, family: string): string {
  return `${first} ${family}`.toLowerCase();
}

/**
 * What typed text looks up: its leading white space dropped, every run of white space inside it read as one
 * space (a trailing one kept, since it ends a whole first name), in lower case.
 *
 * @param typed The text as typed.
 * @returns The prefix, empty when nothing but white space was typed.
 * @example
 * lookupPrefix("  Kaelen   Ash");
 * // "kaelen ash"
 */
export function lookupPrefix(typed: string): string {
  return typed.replace(LEADING_SPACE, "").replace(SPACE_RUN, " ").toLowerCase();
}

/**
 * Whether a full name starts with what was typed, ignoring case and runs of spaces.
 *
 * @param first The first name.
 * @param family The family name.
 * @param typed The text as typed.
 * @returns True when the full name's key starts with the typed text's prefix.
 */
export function matchesPrefix(first: string, family: string, typed: string): boolean {
  return fullNameKey(first, family).startsWith(lookupPrefix(typed));
}
